import React from "react";
import { motion } from "framer-motion";
import { Mail, UserPlus, MessageSquare, Calendar, Trophy, AlertTriangle, ArrowRight, Sparkles } from "lucide-react";

const chaos = [
  { icon: UserPlus, label: "Invite suppliers individually" },
  { icon: MessageSquare, label: "Follow up repeatedly" },
  { icon: Mail, label: "Receive offers via email" },
  { icon: MessageSquare, label: "Manage conversations" },
  { icon: Calendar, label: "Track deadlines manually" },
  { icon: Trophy, label: "Select final supplier" },
];

export default function Problem() {
  return (
    <section
      data-testid="problem-section"
      id="problem"
      className="relative py-24 md:py-32 bg-[#F8FAFC] border-y border-slate-200/60"
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          className="max-w-2xl"
        >
          <p className="font-inter text-[11px] font-bold tracking-[0.22em] uppercase text-[#3B82F6]">
            The Problem
          </p>
          <h2 className="mt-3 font-display text-3xl md:text-[42px] font-extrabold tracking-tight text-[#0B1F3A] leading-[1.1]">
            Managing supplier bidding through emails<br className="hidden md:block" />
            <span className="text-[#475569]">is slow and inefficient.</span>
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#475569] leading-relaxed max-w-xl">
            Traditional procurement requires inviting suppliers one by one, chasing responses,
            collecting offers across email threads, and tracking deadlines by hand — all before
            you can even award a winning manufacturer. The problem is not organizing offers —
            it is managing the bidding process.
          </p>
        </motion.div>

        {/* Chaos workflow */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1], delay: 0.1 }}
          className="mt-14 relative rounded-[24px] bg-white border border-slate-200/80 cb-soft-shadow p-6 md:p-10"
        >
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:flex lg:flex-1 gap-3 lg:gap-2">
              {chaos.map(({ icon: Icon, label }, i) => (
                <React.Fragment key={label}>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                    className="group flex-1 flex flex-col items-center justify-center gap-2 p-4 rounded-xl border border-dashed border-slate-200 bg-[#F8FAFC] min-h-[96px]"
                  >
                    <span className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-[#64748B] group-hover:text-[#0B1F3A] transition-colors">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-[11.5px] font-semibold text-[#475569] text-center leading-snug">{label}</span>
                  </motion.div>
                  {i < chaos.length - 1 && (
                    <ArrowRight className="hidden lg:block h-4 w-4 text-slate-300 self-center shrink-0" />
                  )}
                </React.Fragment>
              ))}
            </div>

            <ArrowRight className="hidden lg:block h-5 w-5 text-slate-300 shrink-0" />

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="lg:w-[260px] rounded-xl border border-amber-200/70 bg-amber-50/60 p-5 flex flex-col items-start gap-2"
            >
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-100 text-amber-700 text-[10px] font-bold tracking-wider uppercase">
                <AlertTriangle className="h-3 w-3" />
                Result
              </span>
              <p className="font-display font-bold text-[#0B1F3A] text-[15px] leading-snug">
                Scattered bidding process
              </p>
              <p className="text-[12px] text-[#475569] leading-relaxed">
                Slow participation. Missed deadlines. No visibility into who is competing.
              </p>
            </motion.div>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-100 flex items-center gap-2.5 text-[12px] text-[#64748B]">
            <Sparkles className="h-3.5 w-3.5 text-[#3B82F6]" />
            <span>
              There is a better way to run online procurement bidding — keep reading.
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
